import { Routes, Router } from '@angular/router';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { AuthGuard } from './core/guards/auth.guard';
import { UnsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { permissionsGuard } from './core/guards/permissions.guard';
import { prospectAccessGuard } from './core/guards/prospect-access.guard';
import { featureFlagGuard, featureFlagMatchGuard } from './core/guards/feature-flag.guard';
import { inject } from '@angular/core';
import { resetToken } from './core/constants/auth-state';
import { FEATURE_FLAGS } from './core/constants/feature-flags.constants';
import { ApplicationReview } from './features/application-review/application-review/application-review';
import { ForgotPassword } from './pages/login/forgot-password/forgot-password';
import { PRODUCT_CODES, PRODUCT_SELECTION_PATH, ROUTE_PRODUCT_DATA_KEY } from './core/constants/product.constants';
import { postLoginRedirectGuard } from './core/guards/post-login-redirect.guard';
import { productAccessGuard } from './core/guards/product-access.guard';
import { productSelectionGuard } from './core/guards/product-selection.guard';

export const routes: Routes = [

  {
    path: '',
    pathMatch: 'full',
    canActivate: [postLoginRedirectGuard],
    children: []
  },
  {
    path: PRODUCT_SELECTION_PATH,
    loadComponent: () =>
      import('./pages/product-selection/product-selection.component')
        .then(m => m.ProductSelectionComponent),
    canActivate: [AuthGuard, productSelectionGuard],
  },
  {
    path: 'forget-password',
    loadComponent: () =>
      import('./pages/login/forgot-password/forgot-password')
        .then(m => m.ForgotPassword),
  },
  {
    path: 'forget-password/:step',
    canActivate: [
      (route) => {
        const router = inject(Router);
        const step = route.paramMap.get('step');

        // Only enforce lockedTimeOut condition for 'reset' step
        if (step === 'reset') {
          if (resetToken()) {
            return true;
          }
          return router.parseUrl('/');
        }

        // Allow navigation for other steps
        return true;
      }
    ],
    loadComponent: () =>
      import('./pages/login/forgot-password/forgot-password')
        .then(m => m.ForgotPassword),
  },
  {
    path: 'reset-password',
    component: ForgotPassword
  },
  //CLIENT LOGIN
  {
    path: 'login/:step',
    loadComponent: () =>
      import('./pages/login/login/login')
        .then(m => m.Login)
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/login/login/login')
        .then(m => m.Login)
  },
  {
    path: 'loggedout',
    loadComponent: () =>
      import('./pages/login/loggedout/loggedout.component')
        .then(m => m.LoggedoutComponent)
  },

  // Opens in a new tab from the Renewals Tracker, without the app menu and header.
  {
    path: 'renewals-tracker/swap-preference/:id',
    loadComponent: () =>
      import('./features/renewals-tracker/components/renewals-swap-preference-form/renewals-swap-preference-form')
        .then(m => m.RenewalsSwapPreferenceForm),
    canActivate: [AuthGuard],
  },

  /*  APP (WITH MAIN LAYOUT) */
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [productAccessGuard],
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import('./features/agent-dashboard/agent-dashboard')
            .then(m => m.AgentDashboardComponent),
        canMatch: [featureFlagMatchGuard],
        canActivate: [permissionsGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          featureName: FEATURE_FLAGS.AGENT_DASHBOARD,
          permissions: ['Dashboard.View', 'Dashboard.Manage']

        }
      },

      {
        path: 'home',
        loadComponent: () =>
          import('./features/home/home.component')
            .then(m => m.HomeComponent),
        canMatch: [featureFlagMatchGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          featureName: FEATURE_FLAGS.HOME_DASHBOARD
        }
      },
      {
        path: 'home',
        redirectTo: 'service-unavailable'
      },

      {
        path: 'pipeline',
        loadComponent: () =>
          import('./features/pipeline-homepage/pipeline-homepage')
            .then(m => m.PipelineHomepage),
        canActivate: [permissionsGuard, featureFlagGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          featureName: FEATURE_FLAGS.LEAD_PIPELINE,
          permissions: ['LeadInfo.Manage']
        }
      },
      {
        path: 'lead-management',
        loadComponent: () =>
          import('./features/lead-management/lead-management')
            .then(m => m.LeadManagementComponent),
        canActivate: [permissionsGuard, featureFlagGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          featureName: FEATURE_FLAGS.LEAD_DETAILS,
          permissions: ['LeadInfo.Review']
        }
      },
      {
        path: 'quote-management',
        loadComponent: () =>
          import('./features/quote-management/quote-management')
            .then(m => m.QuoteManagement),
        canActivate: [permissionsGuard, featureFlagGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          featureName: FEATURE_FLAGS.QUOTE_MANAGEMENT,
          permissions: ['QuoteManagement.View']
        }
      },
      {
        path: 'policy-management',
        loadComponent: () =>
          import('./features/policy-management/policy-management')
            .then(m => m.PolicyManagement),
        canActivate: [permissionsGuard, featureFlagGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          breadcrumb: 'Policy Management',
          featureName: FEATURE_FLAGS.POLICY_MANAGEMENT,
          permissions: ['Policy.View', 'Policy.ViewAll', 'Policy.ViewSelected']
        }
      },
      {
        path: 'quote-builder',
        loadComponent: () =>
          import('./features/quote-builder/quote-builder-home/quote-builder-home')
            .then(m => m.QuoteBuilderHome),
        canActivate: [permissionsGuard, prospectAccessGuard, featureFlagGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          featureName: FEATURE_FLAGS.QUOTE_BUILDER,
          permissions: ['LeadInfo.Manage']
        }
      },
      {
        path: 'preference-form',
        loadComponent: () => import('./features/preference-form/preference-form.component').then(m => m.PreferenceFormComponent),
        canActivate: [permissionsGuard, prospectAccessGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['LeadInfo.Manage']
        }
      },
      {
        path: 'preference-form/:id',
        loadComponent: () => import('./features/preference-form/preference-form.component').then(m => m.PreferenceFormComponent),
        canActivate: [permissionsGuard, prospectAccessGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['LeadInfo.Manage']
        }
      },
      {
        path: 'comparison',
        loadComponent: () =>
          import('./features/quote-builder/quote-builder-home/quote-builder-home')
            .then(m => m.QuoteBuilderHome),
        canActivate: [permissionsGuard, prospectAccessGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          isComparison: true,
          permissions: ['LeadInfo.Manage']
        }
      },
      {
        path: 'maf-template-designer',
        loadComponent: () => import('./features/maf-template-designer/maf-template-designer-grid/maf-template-designer-grid').then(m => m.MafTemplateDesignerGrid),
        canActivate: [featureFlagGuard],
        data: {
          featureName: FEATURE_FLAGS.MAF_TEMPLATE_DESIGNER
        }
      },
      {
        path: 'maf-template-designer/new',
        loadComponent: () => import('./features/maf-template-designer/maf-template-designer').then(m => m.MafTemplateDesigner),
        canActivate: [featureFlagGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          featureName: FEATURE_FLAGS.MAF_TEMPLATE_DESIGNER
        }
      },
      {
        path: 'maf-template-designer/edit/:id',
        loadComponent: () => import('./features/maf-template-designer/maf-template-designer').then(m => m.MafTemplateDesigner),
        canActivate: [featureFlagGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          featureName: FEATURE_FLAGS.MAF_TEMPLATE_DESIGNER
        }
      },
      {
        path: 'lead-detail',
        data: { [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH },
        loadComponent: () => import('./features/lead-detail-view/lead-detail-view').then(m => m.LeadDetailView)
      },
      {
        path: 'service-unavailable',
        loadComponent: () =>
          import('./pages/service-unavailable/service-unavailable.component')
            .then(m => m.ServiceUnavailableComponent)
      },
      {
        path: 'motor',
        loadChildren: () =>
          import('./products/motor/motor.routes')
            .then(m => m.MOTOR_ROUTES),
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.MOTOR,
          breadcrumb: 'Motor Insurance'
        }
      },

      // MAF
      {
        path: 'maf-entry',
        loadComponent: () =>
          import('./features/maf/maf-entry-screen/maf-entry-screen')
            .then(m => m.MafEntryScreen),
        canActivate: [permissionsGuard, prospectAccessGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['Maf.Manage']
        }
      },
      {
        path: 'maf-submission',
        loadComponent: () =>
          import('./features/maf/maf-submit-application/maf-submit-application')
            .then(m => m.MafSubmitApplication),
        canActivate: [permissionsGuard, prospectAccessGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['Maf.Manage']
        },
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        path: 'maf-offline-upload',
        loadComponent: () =>
          import('./features/maf/maf-offline-upload/maf-offline-upload')
            .then(m => m.MafOfflineUpload),
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['Maf.Manage']
        },
        canActivate: [permissionsGuard, prospectAccessGuard],
      },
      {
        path: 'maf-online',
        loadComponent: () =>
          import('./features/maf/maf-online/maf-online/maf-online')
            .then(m => m.MafOnline),
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['Maf.Manage']
        },
        canActivate: [permissionsGuard, prospectAccessGuard],
      },
      {
        path: 'maf-online-preview',
        loadComponent: () =>
          import('./features/maf/maf-online/maf-online-preview-download/maf-online-preview-download')
            .then(m => m.MafOnlinePreviewDownload),
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['Maf.Manage']
        },
        canActivate: [permissionsGuard, prospectAccessGuard],
      },
      {
        path: 'maf-online-upload',
        loadComponent: () =>
          import('./features/maf/maf-online/maf-online-upload/maf-online-upload')
            .then(m => m.MafOnlineUpload),
        canActivate: [permissionsGuard, prospectAccessGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['Maf.Manage']
        },
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        path: 'maf-uploads',
        loadComponent: () =>
          import('./features/maf/maf-upload-center/maf-upload-center')
            .then(m => m.MafUploadCenter),
        canActivate: [permissionsGuard, prospectAccessGuard],
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          permissions: ['Maf.Manage']
        },
        canDeactivate: [UnsavedChangesGuard],

      },
      {
        path: 'review-queue',
        data: {
          [ROUTE_PRODUCT_DATA_KEY]: PRODUCT_CODES.RETAIL_HEALTH,
          breadcrumb: 'Review Queue'
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./features/application-review/review-queue/review-queue')
                .then(m => m.ReviewQueue),
            canActivate: [permissionsGuard, featureFlagGuard],

            data: {
              featureName: FEATURE_FLAGS.REVIEW_QUEUE,
              permissions: ['ReviewQueue.Manage', 'ReviewQueue.View']
            }
          },
          {
            path: 'application-review',
            component: ApplicationReview,
            canActivate: [permissionsGuard],
            canDeactivate: [UnsavedChangesGuard],
            data: {
              breadcrumb: 'Application Review',
              permissions: ['ApplicationReview.View']
            },
            children: [
              {
                path: 'maf-online',
                data: {
                  breadcrumb: 'MAF Application',
                  featureName: FEATURE_FLAGS.REVIEW_QUEUE
                },
                loadComponent: () =>
                  import('./features/application-review/application-review/maf-online-review-edit/maf-online-review-edit')
                    .then(m => m.MafOnlineReviewEdit),
                canActivate: [featureFlagGuard],
                canDeactivate: [UnsavedChangesGuard],
              }
            ]
          }
        ]
      },
      {
        path: 'audit-trail',
        loadComponent: () =>
          import('./features/audit-trail/audit-trail')
            .then(m => m.AuditTrail),
        canActivate: [permissionsGuard, featureFlagGuard],
        data: {
          featureName: FEATURE_FLAGS.AUDIT_TRAIL,
          permissions: ['AuditTrail.View']

        }
      },
      {
        path: 'user-management',
        loadComponent: () =>
          import('./features/user-management/user-management')
            .then(m => m.UserManagement),
        canActivate: [permissionsGuard, featureFlagGuard],
        data: {
          featureName: FEATURE_FLAGS.USER_MANAGEMENT,
          permissions: ['User.ViewAll']

        }
      },
      {
        path: 'contact-management',
        data: {
          breadcrumb: 'Contact Management'
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./features/contact-management/contact-management')
                .then(m => m.ContactManagement),
            canActivate: [permissionsGuard, featureFlagGuard],
            data: {
              featureName: FEATURE_FLAGS.CONTACT_MANAGEMENT,
              permissions: ['ContactManagement.View']
            }
          },
          {
            path: 'contact-list',
            loadComponent: () =>
              import('./features/contact-management/contact-list/contact-list')
                .then(m => m.ContactList),
            canActivate: [permissionsGuard, featureFlagGuard],
            data: {
              featureName: FEATURE_FLAGS.CONTACT_MANAGEMENT,
              breadcrumb: 'Contact Details',
              permissions: ['ContactManagement.View']
            }
          }
        ]
      },
      {
        path: 'renewals-tracker',
        loadComponent: () =>
          import('./features/renewals-tracker/renewals-tracker')
            .then(m => m.RenewalsTracker),
        canActivate: [featureFlagGuard],
        data: {
          // featureName: FEATURE_FLAGS.RENEWALS_TRACKER,
          breadcrumb: 'Renewals Tracker'
        }
      }
    ]
  },

  { path: '**', redirectTo: '' }
];
